// /audit — your own audit trail (tenant_admin sees the tenant-wide
// trail via /v1/audit/tenant; everyone else sees /v1/audit/me).

import { apiFetch } from '@/lib/api';
import { dateOnly, timeShort, ts } from '@/lib/format';
import { ObjectHeader, Pill, SrcTag, type Tone } from '@/primitives';
import { useAuth } from '@/state/auth';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/_authed/audit')({
  component: AuditPage,
});

interface AuditEvent {
  event_id: string;
  ts: string;
  actor_id: string | null;
  actor_label: string;
  action: string;
  action_class: 'DATA' | 'AI' | 'BID' | 'AUTH' | 'CASE' | 'ADMIN';
  result: 'success' | 'failure' | 'blocked';
  resource_type: string | null;
  resource_id: string | null;
  ip: string | null;
  request_id: string | null;
  metadata: Record<string, unknown> | null;
}

const CLASSES = ['DATA', 'AI', 'BID', 'AUTH', 'CASE', 'ADMIN'] as const;

function tagTone(c: AuditEvent['action_class']): 'pri' | 'grn' | 'amb' | 'rd' | 'mag' | undefined {
  switch (c) {
    case 'AUTH':
      return 'amb';
    case 'AI':
      return 'mag';
    case 'BID':
      return 'pri';
    case 'ADMIN':
      return 'rd';
    case 'CASE':
      return 'grn';
    default:
      return undefined;
  }
}

function resultTone(r: AuditEvent['result']): Tone {
  return r === 'success' ? 'green' : r === 'blocked' ? 'amber' : 'red';
}

function AuditPage() {
  const me = useAuth((s) => s.me);
  const [scope, setScope] = useState<'me' | 'tenant'>('me');
  const [filter, setFilter] = useState<(typeof CLASSES)[number] | null>(null);
  const isAdmin = me?.role === 'tenant_admin' || me?.role === 'sys_admin';

  const url = `/v1/audit/${scope}?limit=200${filter ? `&action_class=${filter}` : ''}`;
  const events = useQuery({
    queryKey: ['audit', scope, filter],
    queryFn: () => apiFetch<{ items: AuditEvent[] }>(url),
  });

  const items = events.data?.items ?? [];

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      {events.isFetching && <div className="progress" />}
      <ObjectHeader
        type="CASE"
        id="AUDIT"
        title="Immutable audit trail"
        sub={`Append-only log · hash-chained · ${scope === 'me' ? 'your activity' : 'tenant-wide'}`}
        pills={[
          { tone: 'primary', label: `${ts(items.length)} events` },
          { tone: 'green', label: 'HASH CHAIN OK' },
        ]}
      />

      <div
        style={{
          display: 'flex',
          gap: 6,
          padding: '8px 12px',
          background: 'var(--bg-pane)',
          borderBottom: '1px solid var(--bd)',
        }}
      >
        <button
          type="button"
          className={`btn xs ${scope === 'me' ? 'pri' : 'gh'}`}
          onClick={() => setScope('me')}
        >
          MY ACTIVITY
        </button>
        {isAdmin && (
          <button
            type="button"
            className={`btn xs ${scope === 'tenant' ? 'pri' : 'gh'}`}
            onClick={() => setScope('tenant')}
          >
            TENANT-WIDE
          </button>
        )}
        <span className="vr" />
        {CLASSES.map((c) => (
          <button
            key={c}
            type="button"
            className={`btn xs ${filter === c ? 'pri' : 'gh'}`}
            onClick={() => setFilter(filter === c ? null : c)}
          >
            {c}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 180 }}>WHEN</th>
              <th style={{ width: 50 }}>CLS</th>
              <th>ACTOR</th>
              <th>ACTION</th>
              <th>RESOURCE</th>
              <th>IP</th>
              <th style={{ width: 80 }}>RESULT</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.event_id}>
                <td className="mn dim">
                  {dateOnly(e.ts)} {timeShort(e.ts)}
                </td>
                <td>
                  <SrcTag tone={tagTone(e.action_class)}>{e.action_class}</SrcTag>
                </td>
                <td className="mn">{e.actor_label}</td>
                <td>{e.action}</td>
                <td className="mn dim">
                  {e.resource_type}::{e.resource_id?.slice(0, 8) ?? '—'}
                </td>
                <td className="mn dim">{e.ip ?? '—'}</td>
                <td>
                  <Pill tone={resultTone(e.result)} dot={false}>
                    {e.result}
                  </Pill>
                </td>
              </tr>
            ))}
            {items.length === 0 && !events.isFetching && (
              <tr>
                <td colSpan={7} className="meta mono" style={{ textAlign: 'center', padding: 32 }}>
                  no events match this filter
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
